import React, { useEffect, useRef, useState } from 'react'

import { useDashboardRedux } from '@hooks'

import CategoryList from './CategoryList'
import styles from './CategorySelector.module.css'

const CategorySelector = ({ menuListRef, onSidebarToggle }) => {
    const [isSticky, setIsSticky] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const { categories, selectedCategory, setSelectedCategory } = useDashboardRedux()
    const categoryRef = useRef(null)
    const placeholderRef = useRef(null)
    const [lastScrollTop, setLastScrollTop] = useState(0)
    const isUserScrollingRef = useRef(false)

    useEffect(() => {
        const handleScroll = () => {
            if (!categoryRef.current || !placeholderRef.current || !menuListRef.current) return

            const categoryTop = placeholderRef.current.getBoundingClientRect().top
            setIsSticky(categoryTop <= 56)

            // Skip automatic category update if user is manually scrolling to a category
            if (isUserScrollingRef.current) return

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            const scrollingDown = scrollTop > lastScrollTop
            setLastScrollTop(scrollTop)

            const baseOffset = isExpanded ? 180 : 90
            const headerHeight = 200

            // according to the scroll direction, select different traversal methods
            if (scrollingDown) {
                // when scrolling down, traverse from the start to the end
                for (let i = 0; i < categories.length; i++) {
                    const category = categories[i]
                    const section = menuListRef.current.querySelector(`[data-category="${category}"]`)
                    if (section) {
                        const { bottom } = section.getBoundingClientRect()
                        // if it is the first category, add the header height
                        const offset = i === 0 ? baseOffset + headerHeight : baseOffset
                        if (bottom >= offset) {
                            if (category !== selectedCategory) {
                                setSelectedCategory(category)
                                const activeCategoryElement = categoryRef.current?.querySelector(
                                    `[data-category-item="${category}"]`
                                )
                                if (activeCategoryElement) {
                                    activeCategoryElement.scrollIntoView({ behavior: 'smooth', inline: 'center' })
                                }
                            }
                            break
                        }
                    }
                }
            } else {
                // when scrolling up, traverse from the end to the start
                for (let i = categories.length - 1; i >= 0; i--) {
                    const category = categories[i]
                    const section = menuListRef.current.querySelector(`[data-category="${category}"]`)
                    if (section) {
                        const { top } = section.getBoundingClientRect()
                        // if it is the first category, add the header height
                        const offset = i === 0 ? baseOffset + headerHeight : baseOffset
                        if (top <= offset) {
                            if (category !== selectedCategory) {
                                setSelectedCategory(category)
                                const activeCategoryElement = categoryRef.current?.querySelector(
                                    `[data-category-item="${category}"]`
                                )
                                if (activeCategoryElement) {
                                    activeCategoryElement.scrollIntoView({ behavior: 'smooth', inline: 'center' })
                                }
                            }
                            break
                        }
                    }
                }
            }
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [categories, selectedCategory, setSelectedCategory, menuListRef, isExpanded, lastScrollTop])

    // when a category is selected, scroll to the corresponding section in `MenuList`
    const handleCategorySelect = category => {
        setSelectedCategory(category)

        if (!menuListRef.current) return

        // find the corresponding category section
        const section = menuListRef.current.querySelector(`[data-category="${category}"]`)

        if (!section) return

        const categoryIndex = categories.indexOf(category)

        // Mark that user is manually selecting a category
        isUserScrollingRef.current = true

        // Scroll the category selector horizontally first (instant to avoid blocking)
        if (categoryRef.current) {
            const activeCategoryElement = categoryRef.current.querySelector(`[data-category-item="${category}"]`)
            if (activeCategoryElement) {
                activeCategoryElement.scrollIntoView({ behavior: 'instant', inline: 'center' })
            }
        }

        // if the selected category is the first category, scroll to the top
        if (categoryIndex === 0) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            })

            setTimeout(() => {
                isUserScrollingRef.current = false
            }, 800)
            return
        }

        // Calculate the desired scroll position
        const baseOffset = isExpanded ? 180 : 90
        const currentCategory = selectedCategory
        const currentIndex = categories.indexOf(currentCategory)
        const isFromFirstCategory = currentIndex === 0

        // Get the actual height of categorySelector when it's not sticky
        let categorySelectorHeight = 0
        if (!isSticky && categoryRef.current) {
            categorySelectorHeight = categoryRef.current.getBoundingClientRect().height
        }

        // Get the actual height of NavBar to ensure content is not blocked
        const navbar = document.querySelector('nav')
        const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0

        // Calculate offset
        // baseOffset accounts for some spacing
        // When sticky: categorySelectorHeight = 0 (it's fixed)
        // When not sticky: categorySelectorHeight > 0 (it's in content flow)
        // navbarHeight always needs to be added as navbar is always fixed at top
        const offset = isFromFirstCategory ? baseOffset - 150 + navbarHeight : baseOffset + categorySelectorHeight

        const extraOffset =
            isFromFirstCategory ?
                isExpanded ? -308
                :   -148
            :   0

        // Add extra spacing to ensure category is fully visible below fixed elements
        const finalOffset = offset + 10

        window.scrollTo({
            top: section.offsetTop - finalOffset + extraOffset,
            behavior: 'smooth',
        })

        setTimeout(() => {
            isUserScrollingRef.current = false
        }, 800)
    }

    return (
        <>
            {/* Placeholder to maintain layout when sticky */}
            <div ref={placeholderRef} className={styles.placeholder}></div>

            <div ref={categoryRef} className={`${styles.container} ${isSticky ? styles.sticky : ''}`}>
                {/* Sidebar button - only shown when sticky and authenticated */}
                {isSticky && onSidebarToggle && (
                    <button className={styles.sidebarButton} onClick={onSidebarToggle}>
                        <i className="bx bx-menu" style={{ fontSize: '24px' }} />
                    </button>
                )}

                {/* Scrollable Category List (Hidden when expanded) */}
                <div className={`${styles.scrollContainer} ${isExpanded ? styles.hidden : ''}`}>
                    <CategoryList
                        categories={categories}
                        isExpanded={false}
                        selectedCategory={selectedCategory}
                        onSelectCategory={handleCategorySelect}
                    />
                </div>

                {/* Expanded Full View */}
                <div className={`${styles.expandedContainer} ${isExpanded ? styles.expanded : styles.hidden}`}>
                    <CategoryList
                        categories={categories}
                        isExpanded={true}
                        selectedCategory={selectedCategory}
                        onSelectCategory={handleCategorySelect}
                    />
                </div>

                {/* Expand Button - commented out */}
                {/* <button className={styles.expandButton} onClick={() => setIsExpanded(!isExpanded)}>
                    <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'}`} />
                </button> */}
            </div>
        </>
    )
}

export default CategorySelector
